import { Box, Flex, Tbody, Td, Tooltip, Tr } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertTriangle, Copy, CircleCheckBig, XOctagon } from 'lucide-react';
import { plural, t } from '@lingui/core/macro';
import React, { useCallback } from 'react';
import type { NatRuleColumn } from '@duke-hq/avetmiss/NATTypes';
import type { ValidationError } from '@duke-hq/avetmiss/validation/fieldValidation';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { getNatColumnWidth, NAT_CELL_PADDING_X, NAT_CELL_PADDING_Y } from './natTableLayout';
import { rowHasLinkableIdentifier } from './rowLinkTargets';

export interface CrossFileRowIssue {
  rule: string;
  missingKey: string;
  severity: 'error' | 'warning';
  targetFile: string;
}

interface NATTableBodyProps {
  parentRef: React.RefObject<HTMLDivElement | null>;
  columns: Record<string, NatRuleColumn>;
  values: Record<string, string | undefined>[];
  onError: (rowIndex: number, columnKey: string) => void;
  isDuplicateRow?: (rowIndex: number) => boolean;
  hasRowFieldErrors?: (rowIndex: number) => boolean;
  getFieldErrors?: (rowIndex: number, field: string) => readonly ValidationError[];
  getCrossFileIssues?: (rowIndex: number) => readonly CrossFileRowIssue[];
  originalRowIndices?: readonly number[] | number[];
  onRowClick?: (rowIndex: number, rowData: Record<string, string | undefined>) => void;
}

export function VirtualizedTableBody({
  parentRef,
  columns,
  values,
  onError,
  isDuplicateRow,
  hasRowFieldErrors,
  getFieldErrors,
  getCrossFileIssues,
  originalRowIndices,
  onRowClick,
}: NATTableBodyProps): React.ReactElement {
  const virtualizer = useVirtualizer({
    count: values.length,
    estimateSize: useCallback(() => 100, []),
    getScrollElement: useCallback(() => parentRef.current, [parentRef]),
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <Tbody width={'full'} position="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
      {virtualItems.map((virtualRow) => {
        const rowData = values[virtualRow.index];

        if (!rowData) {
          return null;
        }

        const originalRowIndex = originalRowIndices?.[virtualRow.index] ?? virtualRow.index;
        const isRowDuplicate = isDuplicateRow?.(originalRowIndex) ?? false;
        const hasRowErrors = hasRowFieldErrors?.(originalRowIndex) ?? false;
        const crossFileIssues = getCrossFileIssues?.(originalRowIndex) ?? [];
        const hasCrossFileErrors = crossFileIssues.some((i) => i.severity === 'error');
        const hasCrossFileWarnings = crossFileIssues.some((i) => i.severity === 'warning');
        const hasAnyCrossFile = hasCrossFileErrors || hasCrossFileWarnings;
        const isClickable =
          onRowClick != null &&
          (isRowDuplicate || hasRowErrors || hasAnyCrossFile || rowHasLinkableIdentifier(rowData));

        return (
          <Tr
            key={`row-${virtualRow.key}`}
            onClick={
              isClickable
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRowClick(originalRowIndex, rowData);
                  }
                : undefined
            }
            cursor={isClickable ? 'pointer' : undefined}
            bg={
              isRowDuplicate && hasRowErrors
                ? 'red.50'
                : isRowDuplicate
                  ? 'orange.50'
                  : hasRowErrors || hasCrossFileErrors
                    ? 'red.25'
                    : hasCrossFileWarnings
                      ? 'orange.50'
                      : virtualRow.index % 2 === 0
                        ? 'bg.subtle'
                        : ''
            }
            position="absolute"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
            w="full"
            borderLeft="4px solid"
            borderLeftColor={
              hasRowErrors || hasCrossFileErrors || (isRowDuplicate && hasRowErrors)
                ? 'red.400'
                : isRowDuplicate || hasCrossFileWarnings
                  ? 'orange.400'
                  : 'green.400'
            }
            _hover={{
              bg:
                isRowDuplicate && hasRowErrors
                  ? 'red.100'
                  : isRowDuplicate
                    ? 'orange.100'
                    : hasRowErrors || hasCrossFileErrors
                      ? 'red.75'
                      : hasCrossFileWarnings
                        ? 'orange.100'
                        : 'gray.50',
            }}
          >
            {isRowDuplicate || hasRowErrors || hasAnyCrossFile ? (
              <Td p={0} w="40px" minW="40px" maxW="40px">
                <Flex align="center" justify="center" h="full" px={2}>
                  <Tooltip
                    label={(() => {
                      const parts: string[] = [];
                      if (isRowDuplicate) {
                        parts.push(t`Duplicate row`);
                      }
                      if (hasRowErrors) {
                        parts.push(t`In-file errors`);
                      }
                      const crossErrorCount = crossFileIssues.filter(
                        (i) => i.severity === 'error',
                      ).length;
                      const crossWarningCount = crossFileIssues.filter(
                        (i) => i.severity === 'warning',
                      ).length;
                      if (crossErrorCount > 0) {
                        parts.push(
                          plural(crossErrorCount, {
                            one: '# cross-file error',
                            other: '# cross-file errors',
                          }),
                        );
                      }
                      if (crossWarningCount > 0) {
                        parts.push(
                          plural(crossWarningCount, {
                            one: '# cross-file warning',
                            other: '# cross-file warnings',
                          }),
                        );
                      }
                      return `${parts.join(' · ')}. ${t`Click row for details.`}`;
                    })()}
                    hasArrow
                  >
                    <Flex align="center" gap={1} cursor="help">
                      {hasRowErrors || hasCrossFileErrors ? (
                        <Box color="red.600">
                          <XOctagon size={iconSize.md} />
                        </Box>
                      ) : isRowDuplicate ? (
                        <Box color="orange.600">
                          <Copy size={iconSize.md} />
                        </Box>
                      ) : (
                        <Box color="orange.600">
                          <AlertTriangle size={iconSize.md} />
                        </Box>
                      )}
                    </Flex>
                  </Tooltip>
                </Flex>
              </Td>
            ) : (
              <Td p={0} w="40px" minW="40px" maxW="40px">
                <Flex align="center" justify="center" h="full">
                  <Box color="green.600">
                    <CircleCheckBig size={iconSize.md} />
                  </Box>
                </Flex>
              </Td>
            )}

            {Object.keys(columns).map((columnKey, index) => {
              const column = columns[columnKey];
              if (!column) {
                return null;
              }
              const cellValue = rowData[columnKey];
              if (cellValue === undefined) {
                return null;
              }

              const fieldValidationErrors = getFieldErrors?.(originalRowIndex, columnKey) || [];
              const hasFieldError = fieldValidationErrors.length > 0;

              return (
                <VirtualFilterItem
                  key={`row-${virtualRow.index}-col-${columnKey}`}
                  virtualRow={virtualRow}
                  item={cellValue?.toString() || ''}
                  measureElement={virtualizer.measureElement}
                  hasFieldError={hasFieldError}
                  fieldValidationErrors={fieldValidationErrors}
                  isRowDuplicate={isRowDuplicate}
                  column={columnKey}
                  width={getNatColumnWidth(columns[columnKey], index, Object.keys(columns).length)}
                  handleFixError={onError}
                  originalRowIndex={originalRowIndex}
                />
              );
            })}
          </Tr>
        );
      })}
    </Tbody>
  );
}

function VirtualFilterItem({
  virtualRow,
  item,
  measureElement,
  hasFieldError,
  fieldValidationErrors,
  isRowDuplicate,
  column,
  width,
  handleFixError,
  originalRowIndex,
}: {
  virtualRow: {
    index: number;
  };
  item: string | undefined;
  measureElement: (element: HTMLElement | null) => void;
  hasFieldError: boolean;
  fieldValidationErrors: readonly ValidationError[];
  isRowDuplicate: boolean;
  column: string;
  width: string;
  handleFixError: (rowIndex: number, columnKey: string) => void;
  originalRowIndex: number;
}): React.ReactElement {
  const cellHasError = hasFieldError || (isRowDuplicate && column === 'clientIdentifier');
  const showErrorIcon = cellHasError || isRowDuplicate;

  // Check if field has only warnings (no errors)
  const hasOnlyWarnings =
    fieldValidationErrors.length > 0 &&
    fieldValidationErrors.every((error) => error.severity === 'warning');
  const hasActualErrors = fieldValidationErrors.some((error) => error.severity === 'error');

  return (
    <Td
      data-index={virtualRow.index}
      ref={measureElement}
      sx={{
        color: hasActualErrors
          ? 'red.600'
          : hasOnlyWarnings
            ? 'orange.600'
            : isRowDuplicate
              ? 'orange.700'
              : 'inherit',
        fontWeight: cellHasError ? 'bold' : isRowDuplicate ? 'medium' : 'normal',
        bg:
          hasActualErrors && isRowDuplicate
            ? 'red.100'
            : hasActualErrors
              ? 'red.50'
              : hasOnlyWarnings && isRowDuplicate
                ? 'orange.100'
                : hasOnlyWarnings
                  ? 'orange.50'
                  : isRowDuplicate
                    ? 'orange.100'
                    : '',
      }}
      maxW={width}
      minW={width}
      w={width}
      p={0}
    >
      <Flex align="center" px={NAT_CELL_PADDING_X} py={NAT_CELL_PADDING_Y} overflow="hidden">
        {showErrorIcon ? (
          <Tooltip
            label={
              (hasActualErrors || hasOnlyWarnings) && isRowDuplicate
                ? hasActualErrors
                  ? t`Field validation error and duplicate record`
                  : t`Field validation warning and duplicate record`
                : (hasActualErrors || hasOnlyWarnings) && fieldValidationErrors.length > 0
                  ? fieldValidationErrors.map((err) => err.message).join('; ')
                  : isRowDuplicate
                    ? t`Part of duplicate record`
                    : hasActualErrors
                      ? t`Field validation error`
                      : t`Field validation warning`
            }
            hasArrow
          >
            <Flex
              align="center"
              cursor="pointer"
              onClick={() => handleFixError(originalRowIndex, column)}
            >
              <Box
                as="span"
                mr={2}
                color={
                  hasActualErrors && isRowDuplicate
                    ? 'red.600'
                    : hasActualErrors
                      ? 'red.600'
                      : hasOnlyWarnings && isRowDuplicate
                        ? 'orange.600'
                        : hasOnlyWarnings
                          ? 'orange.600'
                          : 'orange.600'
                }
              >
                {hasActualErrors || hasOnlyWarnings ? (
                  <AlertTriangle size={iconSize.md} />
                ) : (
                  <Copy size={iconSize.md} />
                )}
              </Box>
              <Box flex="1" minW={0} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                {item != null ? item : <em style={{ color: 'gray' }}>empty</em>}
              </Box>
            </Flex>
          </Tooltip>
        ) : (
          <Box flex="1" minW={0} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
            {item != null ? item : <em style={{ color: 'gray' }}>empty</em>}
          </Box>
        )}
      </Flex>
    </Td>
  );
}

export const NATTableBody = React.memo(VirtualizedTableBody);
